import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCw, Square, Play, Terminal, Clock, Server, Box, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getSchool, getContainerStatus, restartContainers, stopContainers, startContainers, getContainerLogs, getSchoolLogs } from '../api';

export default function SchoolDetail() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [containers, setContainers] = useState({});
  const [logs, setLogs] = useState('');
  const [logService, setLogService] = useState('backend');
  const [deployLogs, setDeployLogs] = useState([]);
  const [actionLoading, setActionLoading] = useState('');

  const loadData = useCallback(() => {
    getSchool(schoolId).then(r => setSchool(r.data)).catch(() => {});
    getContainerStatus(schoolId).then(r => setContainers(r.data.containers || {})).catch(() => {});
    getSchoolLogs(schoolId).then(r => setDeployLogs(r.data.results || r.data || [])).catch(() => {});
  }, [schoolId]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadLogs = () => {
    getContainerLogs(schoolId, logService, 100).then(r => setLogs(r.data.logs || '')).catch(() => setLogs('Failed to fetch logs'));
  };

  useEffect(() => { loadLogs(); }, [logService]);

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      if (action === 'restart') await restartContainers(schoolId);
      else if (action === 'stop') await stopContainers(schoolId);
      else if (action === 'start') await startContainers(schoolId);
      setTimeout(loadData, 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading('');
    }
  };

  const services = ['frontend', 'backend', 'db'];

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/schools')}>
            <ArrowLeft size={15} /> Back
          </button>
        </div>
        <h2>{school?.name || schoolId}</h2>
        <p>{school?.domain || `${schoolId}.wyzcon.com`}</p>
      </div>

      {/* Container Status */}
      <div className="card mb-4">
        <div className="card-header">
          <h3><Box size={18} /> Containers</h3>
          <div className="btn-group">
            <button className="btn btn-success btn-sm" onClick={() => handleAction('start')} disabled={!!actionLoading}>
              <Play size={14} /> {actionLoading === 'start' ? '...' : 'Start'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => handleAction('restart')} disabled={!!actionLoading}>
              <RefreshCw size={14} /> {actionLoading === 'restart' ? '...' : 'Restart'}
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => handleAction('stop')} disabled={!!actionLoading}>
              <Square size={14} /> {actionLoading === 'stop' ? '...' : 'Stop'}
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="container-grid">
            {services.map(svc => {
              const c = containers[svc] || {};
              return (
                <div key={svc} className="container-card">
                  <div className="container-header">
                    <span className="container-name">
                      <Server size={16} />
                      {svc}
                    </span>
                    <span className={`badge ${c.running ? 'running' : 'stopped'}`}>
                      {c.running ? 'Running' : c.status || 'Unknown'}
                    </span>
                  </div>
                  {c.cpu_percent && (
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      CPU: {c.cpu_percent}% · Mem: {c.memory_usage || '—'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Logs */}
      <div className="card mb-4">
        <div className="card-header">
          <h3><Terminal size={18} /> Logs</h3>
          <div className="btn-group">
            {services.map(svc => (
              <button
                key={svc}
                className={`btn btn-sm ${logService === svc ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setLogService(svc)}
              >
                {svc}
              </button>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={loadLogs}>
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="log-viewer">{logs || 'No logs available'}</div>
        </div>
      </div>

      {/* Deployment History */}
      <div className="card">
        <div className="card-header">
          <h3><Clock size={18} /> Deployment History</h3>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>Status</th>
                <th>Message</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {deployLogs.map((log, i) => (
                <tr key={i}>
                  <td className="text-sm" style={{ textTransform: 'capitalize', fontWeight: 600 }}>{log.action}</td>
                  <td><span className={`badge ${log.status}`}>{log.status}</span></td>
                  <td className="text-sm text-secondary">{log.message?.substring(0, 80)}</td>
                  <td className="text-sm font-mono text-muted">{new Date(log.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {deployLogs.length === 0 && (
                <tr><td colSpan={4} className="text-muted" style={{ textAlign: 'center', padding: 32 }}>No deployment history</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
